import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Briefcase, Calendar, Building2, 
  CheckCircle, TrendingUp, Globe, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  { name: 'Banking & Finance', icon: TrendingUp },
  { name: 'Manufacturing', icon: Building2 },
  { name: 'Retail & E-commerce', icon: Globe },
  { name: 'Healthcare', icon: Zap }
];

const benefits = [
  {
    title: 'Cost Optimization',
    description: 'Reduce operational costs through automation and process optimization'
  },
  {
    title: 'Faster Time-to-Market',
    description: 'Agile development methodologies for quick product launches'
  },
  {
    title: 'Scalable Architecture',
    description: 'Solutions designed to grow with your business needs'
  },
  {
    title: 'Data-Driven Insights',
    description: 'Analytics and BI solutions for informed decision making'
  }
];

export default function PrivateProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects?category=private`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div data-testid="private-projects-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900" data-testid="private-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Briefcase className="w-10 h-10 text-nstu-gold-400" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Enterprise IT Solutions
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              Empowering private sector clients with innovative, scalable, and secure technology 
              solutions. From ERP implementations to custom software development, we help businesses 
              achieve digital excellence.
            </p>
            <Link to="/contact">
              <Button className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="private-hero-cta">
                Request a Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-12 bg-white border-b" data-testid="industries">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-sm text-gray-500 uppercase tracking-widest mb-8">
            Industries We Serve
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry) => {
              const IconComponent = industry.icon;
              return (
                <div 
                  key={industry.name}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-nstu-green-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-nstu-green-800 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-medium text-gray-700">{industry.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50" data-testid="private-projects-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-green-100 text-nstu-green-800 mb-4">Our Work</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              Enterprise Project Portfolio
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Showcasing our successful implementations for private sector clients across industries
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse overflow-hidden">
                  <div className="aspect-video bg-gray-200" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-3 w-3/4" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <Card 
                  key={project.id}
                  className="group overflow-hidden border border-gray-200 hover:border-nstu-gold-400 transition-all card-hover"
                  data-testid={`private-project-card-${project.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge variant="secondary" className="bg-gray-900 text-white">
                        <Briefcase className="w-3 h-3 mr-1" />
                        Enterprise
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{project.year}</span>
                      <span className="mx-2">•</span>
                      <Building2 className="w-4 h-4" />
                      <span>{project.client}</span>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-nstu-green-800 mb-3">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span 
                          key={tech}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white" data-testid="benefits">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-nstu-gold-100 text-nstu-gold-700 mb-4">Business Value</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-6">
                Transform Your Business with Technology
              </h2>
              <p className="text-gray-600 mb-8">
                Our enterprise solutions are designed to deliver measurable business outcomes. 
                We focus on creating value through technology that drives growth, efficiency, 
                and competitive advantage.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-nstu-gold-500 mb-3" />
                    <h3 className="font-semibold text-nstu-green-800 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800"
                alt="Enterprise Solutions"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services for Enterprise */}
      <section className="py-20 bg-gray-900" data-testid="enterprise-services">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-400 text-nstu-green-900 mb-4">Solutions</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Enterprise Solutions We Offer
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'ERP Implementation',
                features: ['SAP', 'Oracle', 'Microsoft Dynamics', 'Custom ERP']
              },
              {
                title: 'Custom Software',
                features: ['Web Applications', 'Mobile Apps', 'API Integration', 'Legacy Modernization']
              },
              {
                title: 'Cloud Solutions',
                features: ['AWS', 'Azure', 'GCP', 'Hybrid Cloud']
              },
              {
                title: 'Data & Analytics',
                features: ['Business Intelligence', 'Data Warehousing', 'Predictive Analytics', 'Dashboards']
              },
              {
                title: 'Cybersecurity',
                features: ['Security Audits', 'Compliance', 'Incident Response', 'Training']
              },
              {
                title: 'IT Infrastructure',
                features: ['Data Centers', 'Network Design', 'Virtualization', 'Backup & Recovery']
              }
            ].map((service) => (
              <Card key={service.title} className="bg-white/5 backdrop-blur border-0 hover:bg-white/10 transition-colors">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-nstu-gold-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white" data-testid="private-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-nstu-green-800 mb-4">
            Ready to Transform Your Enterprise?
          </h2>
          <p className="text-gray-600 mb-8">
            Let's discuss how our enterprise solutions can help achieve your business objectives
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-nstu-green-800 text-white hover:bg-nstu-green-700 btn-lift" data-testid="private-bottom-cta">
                Schedule a Discussion
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/government-projects">
              <Button size="lg" variant="outline" className="border-nstu-green-800 text-nstu-green-800 hover:bg-nstu-green-800 hover:text-white">
                View Government Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
